"use client"
import { StarIcon,  } from "lucide-react";
import React, { useRef } from "react";
import { Card, CardContent } from '@/components/ui/card';
import { THome_Config } from "@/app/config";
import { motion, useInView } from "motion/react";

export const TestimonialsSection = ({config}:{config:THome_Config['testimonials']})=> {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = config.testimonialsCard;
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6,
      },
    },
  };

  const renderStars = (rating: number, cardIndex: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <motion.div
        key={index}
        variants={{
          hidden: { 
            opacity: 0, 
            scale: 0,
            rotate: -180,
          },
          visible: {
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
              duration: 0.5,
              type: "spring",
              stiffness: 200,
              damping: 10,
            },
          },
        }}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        transition={{
          delay: 0.8 + cardIndex * 0.1 + index * 0.1,
        }}
        whileHover={{
          scale: 1.2,
          rotate: 360,
          transition: { duration: 0.3 }
        }}
      >
        <StarIcon
          className={`w-3.5 h-3.5 md:w-4 md:h-4 lg:w-3.5 lg:h-3.5 ${
            index < rating
              ? "fill-yellow-400 text-yellow-400"
              : "fill-gray-300 text-gray-300"
          }`}
        />
      </motion.div>
    ));
  };

  return (
    <motion.section 
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
      className="w-full py-12 md:py-20 lg:py-[84px] bg-[#f8fafb] flex justify-center"
    >
      <div className="max-w-[1120px] w-full px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          variants={containerVariants}
          className="flex flex-col items-center gap-4 md:gap-6 lg:gap-[13px] mb-8 md:mb-12 lg:mb-14"
        >
          <motion.div 
            variants={{
              hidden: { 
                opacity: 0, 
                y: 30,
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                },
              },
            }}
            className="flex flex-col items-center w-full"
          >
            <h2 className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-2xl sm:text-3xl md:text-4xl lg:text-[31.5px] text-center tracking-[0] leading-tight lg:leading-[35px] px-4 md:px-0">
              {config.title}
            </h2>
          </motion.div>

          <motion.div 
            variants={{
              hidden: { 
                opacity: 0, 
                y: 30,
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                },
              },
            }}
            className="flex flex-col items-center w-full px-4 md:px-0"
          >
            <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-sm md:text-base lg:text-[15.8px] text-center tracking-[0] leading-relaxed lg:leading-[24.5px]">
              {config.description}
            </p>
          </motion.div>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div 
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-7"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={ {
                hidden: { 
                  opacity: 0, 
                  y: 50,
                  scale: 0.95,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: [0.25, 0.25, 0.25, 0.75],
                  },
                },
              }}
              custom={index}
              whileHover={{ 
                y: -8,
                scale: 1.02,
                transition: { 
                  duration: 0.3,
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
            >
              <Card className="flex flex-col min-h-[300px] rounded-xl lg:rounded-[12.75px] border border-solid border-gray-200 hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                <CardContent className="flex flex-col items-start gap-4 md:gap-5 lg:gap-3.5 p-6 md:p-8 lg:pt-7 lg:pb-[21px] lg:px-7 h-full">
                  {/* Star Rating */}
                  <motion.div 
                    className="flex items-center gap-1 lg:gap-[3.5px] w-full"
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={containerVariants}
                  >
                    {renderStars(testimonial.rating, index)}
                  </motion.div>

                  {/* Testimonial Text */}
                  <motion.div 
                    variants={{
                      hidden: { 
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.2,
                        },
                      },
                    }}
                    className="flex flex-col items-start w-full flex-1"
                  >
                    <blockquote className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#354152] text-sm md:text-base lg:text-[13.7px] tracking-[0] leading-relaxed lg:leading-[22.8px] break-words">
                      {testimonial.text}
                    </blockquote>
                  </motion.div>

                  {/* Author Info */}
                  <motion.div 
                    variants={{
                      hidden: { 
                        opacity: 0,
                        x: -20,
                      },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.4,
                        },
                      },
                    }}
                    className="flex items-center gap-3 md:gap-4 lg:gap-3.5 pt-2 lg:pt-[7px] w-full mt-auto"
                  >
                    {/* Avatar */}
                    <motion.div
                      variants={ {
                        hidden: { 
                          opacity: 0, 
                          scale: 0,
                        },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                            duration: 0.6,
                            type: "spring",
                            stiffness: 150,
                            delay: 0.3,
                          },
                        },
                      }}
                      whileHover={{
                        scale: 1.1,
                        rotate: 5,
                        transition: { duration: 0.3 }
                      }}
                      className={`flex w-16 h-16 items-center justify-center ${testimonial.bgColor} rounded-full flex-shrink-0 overflow-hidden`}
                    >
                      <img
                        className="w-16 h-16 rounded-full flex-shrink-0 object-cover"
                        src={testimonial.imageUrl}
                        alt={testimonial.name}
                      />
                    </motion.div>

                    {/* Author Details */}
                    <motion.div 
                      className="flex flex-col items-start flex-1 min-w-0"
                      variants={{
                        hidden: { 
                          opacity: 0,
                          y: 20,
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.6,
                            ease: "easeOut",
                            delay: 0.2,
                          },
                        },
                      }}
                    >
                      <div className="flex flex-col items-start w-full">
                        <h3 className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm md:text-base lg:text-sm tracking-[0] leading-tight lg:leading-[21px] break-words">
                          {testimonial.name}
                        </h3>
                      </div>

                      <div className="flex flex-col items-start w-full">
                        <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-xs md:text-sm lg:text-[12.3px] tracking-[0] leading-tight lg:leading-[17.5px] break-words">
                          {testimonial.title}
                        </p>
                      </div>
                    </motion.div>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};